import { AdresPresentation, AuthContext, SettingsContext, SyncHeightProps } from "adviesbox-shared";
import { FormikContextType, connect, getIn } from "formik";
import React, { InputHTMLAttributes, ReactElement, useContext, useRef } from "react";

import { AdresType } from "../../types";
import LabeledHdnKeuzelijst from "../hdn-keuzelijst/LabeledHdnKeuzelijst";
import getPostcodeInfo from "./get-postcode-info";
import { hasValue } from "../../utils/helpers";

export type AdresProps = {
  readonly?: boolean;
  name: string;
  plaatsCaption?: string;
  landCaption?: string;
  straatCaption?: string;
  metLand?: boolean;
  metHuisEnToevoeging?: boolean;
  metStraat?: boolean;
  fieldSize: "xl" | "l" | "m" | "s" | "grid-cell";
  labelColSize?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  onAddressChanged?: () => void;
} & InputHTMLAttributes<HTMLInputElement>;

const AdresInputComponent = ({
  readonly,
  name,
  formik,
  plaatsCaption,
  landCaption,
  labelColSize = 5,
  fieldSize = "xl",
  sourceRef,
  targetRef,
  metLand = true,
  straatCaption,
  metHuisEnToevoeging = true,
  metStraat = true,
  onAddressChanged
}: AdresProps & SyncHeightProps & { formik: FormikContextType<{}> }): ReactElement => {
  const { user } = useContext(AuthContext);
  const settings = useContext(SettingsContext);
  const adres = getIn(formik.values, name) as AdresType;

  let postcode = "";
  let huisnummer: number | null = null;
  let huisletter = "";
  let landCode = "";

  if (hasValue(adres)) {
    postcode = adres.postcode?.toUpperCase().replace(/\s/g, "");
    huisnummer = adres.huisnummer;
    huisletter = adres.toevoeging;
    landCode = adres.landCode;
  }

  const previousPostcodeHuisnummer = useRef({ postcode, huisnummer, huisletter });

  async function postcodeHuisnummerChanged(): Promise<void> {
    const postcodeError = hasValue(getIn(formik.errors, `${name}.postcode`));
    if (landCode === "NL" && adres.postcode && huisnummer && !postcodeError) {
      if (
        postcode === previousPostcodeHuisnummer.current.postcode &&
        huisnummer === previousPostcodeHuisnummer.current.huisnummer &&
        huisletter === previousPostcodeHuisnummer.current.huisletter
      ) {
        return;
      }

      previousPostcodeHuisnummer.current = {
        postcode,
        huisnummer,
        huisletter
      };

      const postcodeInfo = await getPostcodeInfo(user, settings, postcode, huisnummer, huisletter);
      if (postcodeInfo) {
        if (postcodeInfo.postcode) {
          postcodeInfo.postcode = postcodeInfo.postcode.toUpperCase().replace(/\s/g, "");
        }

        if (
          postcodeInfo.huisnummer === previousPostcodeHuisnummer.current.huisnummer &&
          postcodeInfo.postcode === previousPostcodeHuisnummer.current.postcode
        ) {
          const currVal = getIn(formik.values, name);
          formik.setFieldValue(`${name}`, {
            ...currVal,
            plaats: postcodeInfo.plaats,
            straat: postcodeInfo.straat
          });
        }
      }

      if (onAddressChanged) {
        /* istanbul ignore next */
        onAddressChanged();
      }
    }
  }
  return (
    <>
      <AdresPresentation
        landCode={landCode}
        name={name}
        onBlur={postcodeHuisnummerChanged}
        readonly={readonly}
        plaatsCaption={plaatsCaption}
        labelColSize={labelColSize}
        fieldSize={fieldSize}
        straatCaption={straatCaption}
        metHuisEnToevoeging={metHuisEnToevoeging}
        metStraat={metStraat}
        sourceRef={sourceRef}
        targetRef={targetRef}
      />
      {metLand && (
        <LabeledHdnKeuzelijst
          caption={landCaption ? landCaption : "Land"}
          labelColSize={labelColSize}
          fieldSize={fieldSize}
          name={`${name}.landCode`}
          keuzelijst="LandType"
          berichtSoortType="AX"
          readonly={readonly}
        />
      )}
    </>
  );
};

export const AdresInput = connect<AdresProps & SyncHeightProps>(AdresInputComponent);
